import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Avatar,
  Card,
  IconButton,
  Skeleton,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  FiberManualRecord as DotIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { useGetPublicHeroBannersQuery } from "@/store/api/homepageApi";

const AUTOPLAY_INTERVAL = 5000;

const HeroBannerSlide = ({ banner }) => {
  return (
    <Card
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        background: "#fff",
        border: "1px solid rgba(0,0,0,0.08)",
        boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
      }}
    >
      {/* Main content row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 2, sm: 3 },
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 2.5 },
        }}
      >
        {/* Avatar */}
        <Avatar
          src={banner.image}
          alt={banner.title}
          sx={{
            width: { xs: 64, sm: 80, md: 96 },
            height: { xs: 64, sm: 80, md: 96 },
            flexShrink: 0,
            border: "2px solid rgba(0,0,0,0.06)",
          }}
        />

        {/* Text */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {banner.description && (
            <Typography
              variant="body2"
              sx={{
                color: "#555",
                fontWeight: 500,
                mb: 0.5,
                fontSize: { xs: "0.78rem", sm: "0.85rem" },
              }}
            >
              {banner.description}
            </Typography>
          )}

          <Typography
            variant="subtitle1"
            sx={{
              color: "#009688",
              fontWeight: 700,
              lineHeight: 1.4,
              fontSize: { xs: "0.88rem", sm: "1rem", md: "1.05rem" },
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {banner.title}
          </Typography>
        </Box>
      </Box>

      {/* Dark bottom bar */}
      <Box
        sx={{
          background: "linear-gradient(90deg, #1a237e 0%, #0d47a1 60%, #01579b 100%)",
          px: { xs: 2, sm: 3 },
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {banner.teacherName && (
            <Typography
              variant="caption"
              sx={{ color: "rgba(255,255,255,0.85)", fontWeight: 500, fontSize: "0.8rem" }}
            >
              {banner.teacherName}
            </Typography>
          )}
          {banner.teacherName && banner.subjectTag && (
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)" }}>
              |
            </Typography>
          )}
          {banner.subjectTag && (
            <Typography
              variant="caption"
              sx={{ color: "rgba(255,255,255,0.75)", fontSize: "0.8rem" }}
            >
              {banner.subjectTag}
            </Typography>
          )}
        </Box>
      </Box>
    </Card>
  );
};

const HeroBannerSkeleton = () => (
  <Skeleton
    variant="rectangular"
    sx={{ width: "100%", height: 130, borderRadius: 2 }}
  />
);

const HeroBannerSection = () => {
  const { data, isLoading, error } = useGetPublicHeroBannersQuery();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const banners = data?.data || data || [];

  const goTo = useCallback(
    (index) => {
      if (banners.length === 0) return;
      setActiveIndex((index + banners.length) % banners.length);
    },
    [banners.length]
  );

  // Autoplay
  useEffect(() => {
    if (banners.length <= 1 || isPaused) return;
    const timer = setInterval(() => goTo(activeIndex + 1), AUTOPLAY_INTERVAL);
    return () => clearInterval(timer);
  }, [activeIndex, isPaused, banners.length, goTo]);

  // Reset index when banners change
  useEffect(() => {
    setActiveIndex(0);
  }, [banners.length]);

  if (isLoading) return <HeroBannerSkeleton />;
  if (error || banners.length === 0) return null;

  const showControls = banners.length > 1;

  return (
    <Box
      sx={{  position: "relative" }}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <HeroBannerSlide banner={banners[activeIndex]} />

      {/* Prev / Next arrows */}
      {showControls && (
        <>
          <IconButton
            onClick={() => goTo(activeIndex - 1)}
            size="small"
            sx={{
              position: "absolute",
              left: 8,
              top: "50%",
              transform: "translateY(-50%)",
              background: "rgba(0,0,0,0.45)",
              color: "#fff",
              "&:hover": { background: "rgba(0,0,0,0.7)" },
              zIndex: 2,
            }}
          >
            <ChevronLeft />
          </IconButton>
          <IconButton
            onClick={() => goTo(activeIndex + 1)}
            size="small"
            sx={{
              position: "absolute",
              right: 8,
              top: "50%",
              transform: "translateY(-50%)",
              background: "rgba(0,0,0,0.45)",
              color: "#fff",
              "&:hover": { background: "rgba(0,0,0,0.7)" },
              zIndex: 2,
            }}
          >
            <ChevronRight />
          </IconButton>

          {/* Dot indicators */}
          <Box
            sx={{
              position: "absolute",
              bottom: 32,
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              gap: 0.5,
              zIndex: 2,
            }}
          >
            {banners.map((_, i) => (
              <DotIcon
                key={i}
                onClick={() => goTo(i)}
                sx={{
                  fontSize: i === activeIndex ? "10px" : "8px",
                  color: i === activeIndex ? "#fff" : "rgba(255,255,255,0.45)",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
              />
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

export default HeroBannerSection;
